import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "motion/react";
import { cn, toastError } from "@strivelabs/ui-core";
import {
  Composer as UIComposer,
  ComposerSurface,
  ComposerChips,
  ComposerSkillChip,
  ComposerAttachments,
  ComposerTextarea,
  ComposerToolbar,
  ComposerAttach,
  ComposerDivider,
  ComposerSpacer,
  ComposerMode,
  ComposerSend,
  ComposerGreeting,
  ComposerDropOverlay,
  ComposerDictationBar,
  ComposerDictateControl,
  type ComposerAttachmentItem,
  type ComposerModeOption,
} from "@strivelabs/ui-blocks";
import { IconButton, Tooltip } from "@strivelabs/ui-core/v2";
import { Loader2, Zap } from "lucide-react";
import { openAttachment, type ImagePreview } from "./image-preview-dialog";
import { DictationDeviceMenu } from "./DictationDeviceMenu";
import { LibraryPickerDialog } from "@/components/library/LibraryPickerDialog";
import { fileContentUrl, type ChatAttachment } from "@/services/DataService";
import { useUploadFile } from "@/hooks/useUploadFile";
import { ANALYTICS_EVENTS, useTrack } from "@/lib/analytics";
import { useRotatingPlaceholder } from "@/hooks/useRotatingPlaceholder";
import {
  useTypewriterPlaceholder,
  type TypewriterPlaceholder,
} from "@/hooks/useTypewriterPlaceholder";
import { useDictation } from "@/hooks/useDictation";
import { computeInsertion } from "@/lib/insertTextAtCursor";
import { logger } from "@/lib/logger";
import {
  CHAT_ALLOWED_FILE_LABEL,
  CHAT_ALLOWED_FILE_MIME_TYPES,
  CHAT_MAX_FILES,
  CHAT_MAX_FILE_SIZE_BYTES,
  FILE_THUMBNAIL_SIZE,
  normalizeUploadFile,
} from "@/constants/fileUpload";
import type { Skill } from "@/constants/skills";

/** Image-only `accept` subset for the Images picker (the allowlist minus PDF). */
const CHAT_IMAGE_ACCEPT = CHAT_ALLOWED_FILE_MIME_TYPES.filter((type) =>
  type.startsWith("image/"),
).join(",");

/** Document-only `accept` subset for the Files picker (the allowlist minus images). */
const CHAT_DOC_ACCEPT = [
  ...CHAT_ALLOWED_FILE_MIME_TYPES.filter((type) => !type.startsWith("image/")),
  ".csv",
].join(",");

/** Stable empty phrase list so a static `inputPlaceholder` never rotates. */
const EMPTY_PHRASES: string[] = [];

/** Stable inert spec so non-typewriter placeholders never animate. */
const EMPTY_TYPEWRITER: TypewriterPlaceholder = { prefix: "", tails: [] };

/** Human-readable file size for an attachment chip. */
function formatBytes(bytes: number): string {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  const size = bytes / 1024 ** exponent;
  return `${size >= 10 || exponent === 0 ? Math.round(size) : size.toFixed(1)} ${units[exponent]}`;
}

interface AttachmentEntry {
  item: ComposerAttachmentItem;
  uploaded?: ChatAttachment;
}

/**
 * A chip for a file already uploaded (picked from the library, or handed in
 * by the host): ready from the start, its thumbnail the stored one.
 */
function readyEntry(file: ChatAttachment): AttachmentEntry {
  const kind = file.mime_type.startsWith("image/") ? "image" : "file";
  return {
    item: {
      id: crypto.randomUUID(),
      kind,
      name: file.filename,
      meta: formatBytes(file.size_bytes),
      status: "ready",
      thumbUrl:
        kind === "image"
          ? fileContentUrl(file.id, FILE_THUMBNAIL_SIZE.CHIP)
          : undefined,
    },
    uploaded: file,
  };
}

/**
 * Let go of a chip's thumbnail. Only a local preview (an object URL made for
 * a file still being picked) holds anything; a stored file's thumbnail is a
 * plain URL.
 */
function releaseThumbnail(entry: AttachmentEntry): void {
  if (entry.item.thumbUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(entry.item.thumbUrl);
  }
}

export interface ComposerProps {
  variant?: "home" | "chat";
  /** Greeting rendered above the composer (home variant). */
  greeting?: React.ReactNode;
  className?: string;
  allowFileUpload: boolean;
  /**
   * Placeholder for the empty input. A string is shown statically; an array of
   * phrases rotates through them on a slow timer (reduced-motion pins the first
   * phrase); a {@link TypewriterPlaceholder} keeps its prefix fixed and types /
   * deletes the continuations after it (reduced-motion pins prefix + last
   * tail). The animation timers live here so they re-render only the composer,
   * not the consuming page.
   */
  inputPlaceholder: string | string[] | TypewriterPlaceholder;
  /**
   * Placeholder shown while a skill chip is attached (defaults to
   * "Add context..."). Home passes its typewriter prefix ("Ask Strive") — per
   * the artifact, an attached skill stops the continuation but keeps the
   * resting text — or the hovered example's preview, which outranks it.
   */
  skillAttachedPlaceholder?: string;
  isLoading: boolean;
  onSubmit: (message: {
    text: string;
    file_ids?: string[];
    attachments?: ChatAttachment[];
    attachedSkillShortName?: string;
  }) => void;
  onPreviewImage?: (preview: ImagePreview) => void;
  onStop?: () => void;
  textareaRef?: React.RefObject<HTMLTextAreaElement>;
  /**
   * External drop zone (for example, the whole chat panel). When provided,
   * drag-and-drop is bound there instead of only the composer surface.
   */
  dropZoneRef?: React.RefObject<HTMLElement | null>;
  onKeyDown?: React.KeyboardEventHandler<HTMLTextAreaElement>;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement>;
  /** Overlay rendered above the composer (for example, a slash-command panel). */
  inputOverlay?: React.ReactNode;
  attachedSkill?: Skill | null;
  onDetachSkill?: () => void;
  skillRemovable?: boolean;
  composeMessage?: (text: string) => string;
  /** When set, the composer is force-disabled and this hint is shown. */
  externalDisabledHint?: string;
  onBrowseSkills?: () => void;
  /** Show the response-mode slider (chat only in phase 1). */
  showModePicker?: boolean;
  /** Response-mode catalog for the slider. */
  modes?: ComposerModeOption[];
  /** Active mode id. */
  mode?: string;
  onModeChange?: (id: string) => void;
  /** Show the mic dictation control (chat + home). Suppressed while disabled. */
  allowDictation?: boolean;
  /**
   * Files already uploaded to start with, as ready chips. Read on mount only:
   * a host that wants them gone after a send simply stops rendering this
   * composer (the chat's empty state does, once the first message is sent).
   */
  initialAttachments?: ChatAttachment[];
}

/**
 * Platform composer. Composes the ui-blocks Composer primitives and wires them
 * to the app: file upload, slash-command handlers, skill badge, response mode,
 * and submit. Presentational chrome lives in ui-blocks; data wiring lives here.
 */
export const Composer: React.FC<ComposerProps> = ({
  variant = "chat",
  greeting,
  className,
  allowFileUpload,
  inputPlaceholder,
  skillAttachedPlaceholder,
  isLoading,
  onSubmit,
  onPreviewImage,
  onStop,
  textareaRef,
  dropZoneRef,
  onKeyDown,
  onChange,
  onBlur,
  onFocus,
  inputOverlay,
  attachedSkill,
  onDetachSkill,
  skillRemovable = true,
  composeMessage,
  externalDisabledHint,
  onBrowseSkills,
  showModePicker = true,
  modes = [],
  mode,
  onModeChange,
  allowDictation = true,
  initialAttachments,
}) => {
  const externalDisabled = !!externalDisabledHint;
  const disabled = isLoading || externalDisabled;
  const uploadFile = useUploadFile();
  const trackEvent = useTrack();

  const typewriterSpec =
    typeof inputPlaceholder === "object" && !Array.isArray(inputPlaceholder)
      ? inputPlaceholder
      : null;
  const rotatingPlaceholder = useRotatingPlaceholder(
    Array.isArray(inputPlaceholder) ? inputPlaceholder : EMPTY_PHRASES,
    { enabled: !attachedSkill },
  );
  const typewriterPlaceholder = useTypewriterPlaceholder(
    typewriterSpec ?? EMPTY_TYPEWRITER,
    { enabled: Boolean(typewriterSpec) && !attachedSkill },
  );
  const resolvedPlaceholder =
    typeof inputPlaceholder === "string"
      ? inputPlaceholder
      : Array.isArray(inputPlaceholder)
        ? rotatingPlaceholder
        : typewriterPlaceholder;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [entries, setEntries] = useState<AttachmentEntry[]>(() =>
    (initialAttachments ?? []).slice(0, CHAT_MAX_FILES).map(readyEntry),
  );
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const [text, setText] = useState("");
  const localTextareaRef = useRef<HTMLTextAreaElement>(null);
  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      localTextareaRef.current = node;
      if (textareaRef)
        (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    },
    [textareaRef],
  );

  const items = entries.map((entry) => entry.item);

  // Latest entries for cleanup/count checks without re-creating `addFiles`.
  const entriesRef = useRef(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  // Revoke any outstanding thumbnail object URLs on unmount.
  useEffect(
    () => () => {
      entriesRef.current.forEach(releaseThumbnail);
    },
    [],
  );

  const addFiles = useCallback(
    async (pickedFiles: File[]) => {
      if (!pickedFiles.length) return;
      const files = pickedFiles.map(normalizeUploadFile);

      // Enforce the backend allowlist/size/count client-side so bad picks are
      // rejected before an upload round-trip (mirrors POST /file).
      const allowed = CHAT_ALLOWED_FILE_MIME_TYPES as readonly string[];
      let slots = CHAT_MAX_FILES - entriesRef.current.length;
      let badType = false;
      let tooBig = false;
      let tooMany = false;
      const accepted: File[] = [];
      for (const file of files) {
        if (!allowed.includes(file.type)) {
          badType = true;
        } else if (file.size > CHAT_MAX_FILE_SIZE_BYTES) {
          tooBig = true;
        } else if (slots <= 0) {
          tooMany = true;
        } else {
          accepted.push(file);
          slots -= 1;
        }
      }
      if (badType) {
        toastError(`Unsupported file type. Allowed: ${CHAT_ALLOWED_FILE_LABEL}.`);
      }
      if (tooBig) {
        toastError(`Each file must be under ${formatBytes(CHAT_MAX_FILE_SIZE_BYTES)}.`);
      }
      if (tooMany) {
        toastError(`You can attach up to ${CHAT_MAX_FILES} files.`);
      }
      if (!accepted.length) return;

      const staged = accepted.map((file) => ({
        id: crypto.randomUUID(),
        file,
        kind: file.type.startsWith("image/")
          ? ("image" as const)
          : ("file" as const),
      }));
      setEntries((prev) => [
        ...prev,
        ...staged.map(({ id, file, kind }) => ({
          item: {
            id,
            kind,
            name: file.name,
            meta: formatBytes(file.size),
            status: "uploading" as const,
            thumbUrl:
              kind === "image" ? URL.createObjectURL(file) : undefined,
          },
        })),
      ]);
      await Promise.all(
        staged.map(async ({ id, file }) => {
          try {
            const uploaded = await uploadFile(file);
            setEntries((prev) =>
              prev.map((entry) =>
                entry.item.id === id
                  ? { item: { ...entry.item, status: "ready" }, uploaded }
                  : entry,
              ),
            );
          } catch (err) {
            logger(err as Error, {
              context: "Composer: attachment upload failed",
            });
            const message =
              err instanceof Error && err.message
                ? err.message
                : "Failed to upload attachment";
            setEntries((prev) =>
              prev.map((entry) =>
                entry.item.id === id
                  ? { item: { ...entry.item, status: "error", error: message } }
                  : entry,
              ),
            );
            toastError(message);
          }
        }),
      );
    },
    [uploadFile],
  );

  // Files picked from the library are already stored: they go in ready, with
  // no upload, held to the same count as any other attachment. One already
  // attached is not attached twice.
  const addExisting = useCallback(
    (files: ChatAttachment[]) => {
      const attached = new Set(
        entriesRef.current.flatMap((entry) =>
          entry.uploaded ? [entry.uploaded.id] : [],
        ),
      );
      const fresh = files.filter((file) => !attached.has(file.id));
      const slots = Math.max(0, CHAT_MAX_FILES - entriesRef.current.length);
      const accepted = fresh.slice(0, slots);
      if (fresh.length > accepted.length) {
        toastError(`You can attach up to ${CHAT_MAX_FILES} files.`);
      }
      if (!accepted.length) return;
      accepted.forEach((file) => {
        trackEvent(ANALYTICS_EVENTS.CHAT_FILE_ATTACHED, {
          file_type: file.mime_type || "unknown",
          file_size_bytes: file.size_bytes,
          source: "library",
        });
      });
      setEntries((prev) => [...prev, ...accepted.map(readyEntry)]);
    },
    [trackEvent],
  );

  // Paste-to-attach: a pasted screenshot or file lands in `clipboardData.files`.
  // Attach those; only swallow the paste when it carries no text, so pasting
  // text (or text-with-file) still lands in the input.
  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      if (!allowFileUpload) return;
      const files = Array.from(event.clipboardData?.files ?? []).filter(
        (file) => file.type,
      );
      if (files.length === 0) return;
      void addFiles(files);
      if (!event.clipboardData?.getData("text")) event.preventDefault();
    },
    [allowFileUpload, addFiles],
  );

  const removeAttachment = useCallback((id: string) => {
    setEntries((prev) => {
      const target = prev.find((entry) => entry.item.id === id);
      if (target) releaseThumbnail(target);
      return prev.filter((entry) => entry.item.id !== id);
    });
  }, []);

  // Panel-wide drag-and-drop: when the host passes a drop zone (for example,
  // the whole chat panel), bind drop there instead of only the composer
  // surface. The composer sits inside the zone, so surface drops still work.
  useEffect(() => {
    const zone = dropZoneRef?.current;
    if (!zone || !allowFileUpload) return;
    const onDragOver = (event: DragEvent) => {
      event.preventDefault();
      setDragActive(true);
    };
    const onDragLeave = (event: DragEvent) => {
      if (!zone.contains(event.relatedTarget as Node | null)) {
        setDragActive(false);
      }
    };
    const onDrop = (event: DragEvent) => {
      event.preventDefault();
      setDragActive(false);
      if (event.dataTransfer) addFiles(Array.from(event.dataTransfer.files));
    };
    zone.addEventListener("dragover", onDragOver);
    zone.addEventListener("dragleave", onDragLeave);
    zone.addEventListener("drop", onDrop);
    return () => {
      zone.removeEventListener("dragover", onDragOver);
      zone.removeEventListener("dragleave", onDragLeave);
      zone.removeEventListener("drop", onDrop);
    };
  }, [dropZoneRef, allowFileUpload, addFiles]);

  // Safety net so the drop overlay can never stick after a drag that ends
  // without a clean dragleave (dropped outside the page, or cancelled via Esc).
  useEffect(() => {
    if (!allowFileUpload) return;
    const reset = () => setDragActive(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDragActive(false);
    };
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [allowFileUpload]);

  const openAttachmentPreview = useCallback(
    (id: string) => {
      const entry = entries.find((item) => item.item.id === id);
      if (!entry?.uploaded) return;
      openAttachment(
        {
          url: fileContentUrl(entry.uploaded.id),
          mediaType: entry.uploaded.mime_type,
          filename: entry.uploaded.filename,
        },
        onPreviewImage,
      );
    },
    [entries, onPreviewImage],
  );

  const insertTranscript = useCallback((transcript: string) => {
    const el = localTextareaRef.current;
    setText((prev) => {
      const start = el?.selectionStart ?? prev.length;
      const end = el?.selectionEnd ?? prev.length;
      const { next, caret } = computeInsertion(prev, start, end, transcript);
      requestAnimationFrame(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(caret, caret);
        }
      });
      return next;
    });
  }, []);

  const dictation = useDictation({ onInsert: insertTranscript });
  const dictationEnabled = allowDictation && !disabled;

  useEffect(() => {
    if (!dictationEnabled) return;
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "d") {
        event.preventDefault();
        if (dictation.status === "recording") dictation.stop();
        else if (dictation.status === "idle") dictation.start();
      }
      if (event.key === "Escape" && dictation.status === "recording") dictation.cancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dictationEnabled, dictation]);

  const handleSubmit = (text: string) => {
    if (externalDisabled) return;
    const finalText = composeMessage ? composeMessage(text) : text;
    const uploaded = entries
      .map((entry) => entry.uploaded)
      .filter((value): value is ChatAttachment => Boolean(value));
    // Guard against an empty send when the only chip left is a failed upload
    // (no ready attachment, no skill, no text).
    if (!finalText.trim() && uploaded.length === 0 && !attachedSkill) return;
    trackEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_SENT, {
      mode,
      message_length: finalText.trim().length,
      attachment_count: uploaded.length,
      used_slash_command: Boolean(attachedSkill),
    });
    onSubmit({
      text: finalText,
      file_ids: uploaded.map((attachment) => attachment.id),
      attachments: uploaded,
      attachedSkillShortName: attachedSkill?.shortName,
    });
    entries.forEach(releaseThumbnail);
    setEntries([]);
    onDetachSkill?.();
  };

  const uploading = entries.some((entry) => entry.item.status === "uploading");

  // Only a fully-uploaded attachment counts as submittable content on its own:
  // a still-uploading or failed chip must not enable an otherwise-empty send,
  // and neither does a bare skill — per the artifact, the send stays muted
  // until text is typed (the skill is a modifier, not a message).
  const hasReadyAttachment = entries.some((entry) => Boolean(entry.uploaded));
  // The chip row, though, opens the moment anything is picked: an upload in
  // progress shows its spinner, and a failed one stays to be read and removed.
  const hasChips = Boolean(attachedSkill) || entries.length > 0;

  return (
    <UIComposer
      className={className}
      value={text}
      onValueChange={setText}
      onSubmit={handleSubmit}
      modes={modes}
      mode={mode}
      onModeChange={onModeChange}
      disabled={disabled}
      busy={isLoading}
      allowEmptySubmit={hasReadyAttachment}
      submitBlocked={uploading}
    >
      {variant === "home" && greeting && (
        <ComposerGreeting>{greeting}</ComposerGreeting>
      )}
      {inputOverlay}
      {externalDisabledHint && (
        <div
          role="status"
          aria-live="polite"
          className="mb-2 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-xs font-medium text-primary"
        >
          <Loader2 className="size-3 animate-spin" aria-hidden="true" />
          <span>{externalDisabledHint}</span>
        </div>
      )}
      <div
        className="relative"
        onDragOver={
          allowFileUpload && !dropZoneRef
            ? (event) => {
                event.preventDefault();
                setDragActive(true);
              }
            : undefined
        }
        onDragLeave={
          allowFileUpload && !dropZoneRef ? () => setDragActive(false) : undefined
        }
        onDrop={
          allowFileUpload && !dropZoneRef
            ? (event) => {
                event.preventDefault();
                setDragActive(false);
                addFiles(Array.from(event.dataTransfer.files));
              }
            : undefined
        }
      >
        <ComposerDropOverlay
          active={dragActive}
          hint={`Up to ${CHAT_MAX_FILES} files · max ${formatBytes(CHAT_MAX_FILE_SIZE_BYTES)} each`}
        />
        <AnimatePresence>
          {dictationEnabled && dictation.status !== "idle" && (
            <ComposerDictationBar
              status={dictation.status}
              elapsedMs={dictation.elapsedMs}
              levels={dictation.levels}
            />
          )}
        </AnimatePresence>
        <ComposerSurface>
          <div
            data-testid="composer-chip-row"
            data-state={hasChips ? "open" : "closed"}
            aria-hidden={!hasChips || undefined}
            className={cn(
              "grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
              hasChips ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
            )}
          >
            <div className="min-h-0 overflow-hidden">
              <ComposerChips>
                {attachedSkill && (
                  <ComposerSkillChip
                    label={attachedSkill.displayName}
                    onRemove={skillRemovable ? onDetachSkill : undefined}
                  />
                )}
                <ComposerAttachments
                  items={items}
                  onRemove={removeAttachment}
                  onOpen={openAttachmentPreview}
                />
              </ComposerChips>
            </div>
          </div>
          <ComposerTextarea
            ref={setTextareaRef}
            placeholder={
              attachedSkill
                ? (skillAttachedPlaceholder ?? "Add context...")
                : resolvedPlaceholder
            }
            animatePlaceholder={!attachedSkill && Array.isArray(inputPlaceholder)}
            onKeyDown={onKeyDown}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onPaste={allowFileUpload ? handlePaste : undefined}
          />
          <ComposerToolbar>
            {allowFileUpload && (
              <ComposerAttach
                onPickFiles={() => fileInputRef.current?.click()}
                onPickImages={() => imageInputRef.current?.click()}
                onPickLibrary={() => {
                  if (entriesRef.current.length >= CHAT_MAX_FILES) {
                    toastError(`You can attach up to ${CHAT_MAX_FILES} files.`);
                  } else {
                    setLibraryOpen(true);
                  }
                }}
                filesHint="PDF"
                imagesHint="PNG, JPG, GIF, WebP"
                libraryHint="Files already uploaded"
              />
            )}
            {onBrowseSkills && (
              <Tooltip label="Insert Skill" shortcut="/">
                <IconButton
                  aria-label="Browse skills"
                  onClick={onBrowseSkills}
                  active={Boolean(attachedSkill)}
                  // With a skill attached the button borrows the skill pill's
                  // exact fill and ink (artifact #skills-button[data-active]),
                  // so the two read as one selected-skill treatment. The
                  // orange holds through hover.
                  className={cn(
                    "relative",
                    attachedSkill &&
                      "data-[active=true]:bg-[rgba(245,107,16,0.10)] data-[active=true]:text-[#F56B10] data-[active=true]:hover:bg-[rgba(245,107,16,0.16)] data-[active=true]:hover:text-[#F56B10]",
                  )}
                >
                  <Zap className="size-[17px]" />
                </IconButton>
              </Tooltip>
            )}
            {/* Needs two modes to be worth showing: the catalog is scoped to the
                conversation's agent, and an agent left with a single mode has
                nothing to pick between. */}
            {showModePicker && modes.length > 1 && (
              <>
                <ComposerDivider />
                <ComposerMode />
              </>
            )}
            <ComposerSpacer />
            {dictationEnabled && (
              <ComposerDictateControl
                status={dictation.status}
                onStart={dictation.start}
                onStop={dictation.stop}
                onDiscard={dictation.cancel}
                deviceMenu={<DictationDeviceMenu />}
              />
            )}
            {!(dictationEnabled && dictation.status !== "idle") && <ComposerSend onStop={onStop} />}
          </ComposerToolbar>
        </ComposerSurface>
      </div>
      {allowFileUpload && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={CHAT_DOC_ACCEPT}
            className="hidden"
            onChange={(event) => {
              addFiles(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
          <input
            ref={imageInputRef}
            type="file"
            multiple
            accept={CHAT_IMAGE_ACCEPT}
            className="hidden"
            onChange={(event) => {
              addFiles(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
          <LibraryPickerDialog
            open={libraryOpen}
            onOpenChange={setLibraryOpen}
            attachedIds={entries.flatMap((entry) =>
              entry.uploaded ? [entry.uploaded.id] : [],
            )}
            maxSelectable={Math.max(0, CHAT_MAX_FILES - entries.length)}
            onAdd={addExisting}
          />
        </>
      )}
    </UIComposer>
  );
};
